import { useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import type { Instance } from 'flatpickr/dist/types/instance';
import { TextInput } from '@carbon/react';

// A multi-date calendar bound to a Carbon text input.
//
// Carbon's own <DatePicker> can't do this: its flatpickr init effect hard-returns for any
// datePickerType other than 'single' or 'range' (see DatePicker.js), so passing "multiple"
// renders a dead calendar icon that never opens. We drive flatpickr directly instead — its
// calendar markup is themed by Carbon's global flatpickr styles already loaded app-wide, so it
// looks native. Selection is restricted to `enabledDates`, and values round-trip as Y-m-d.

interface Props {
  id: string;
  labelText: string;
  size?: 'sm' | 'md' | 'lg';
  value: string[]; // selected dates, Y-m-d
  enabledDates: string[]; // the only dates the calendar allows (backend as-of dates)
  onChange: (dates: string[]) => void;
  placeholder?: string;
}

const iso = (inst: Instance, d: Date) => inst.formatDate(d, 'Y-m-d');
const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

export function MultiDatePicker({
  id,
  labelText,
  size = 'sm',
  value,
  enabledDates,
  onChange,
  placeholder = 'Select dates',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const fpRef = useRef<Instance | null>(null);
  // Keep the latest onChange without re-initializing flatpickr.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Initialize flatpickr once, on mount.
  useEffect(() => {
    if (!inputRef.current) return undefined;
    const fp = flatpickr(inputRef.current, {
      mode: 'multiple',
      dateFormat: 'Y-m-d',
      defaultDate: value,
      enable: enabledDates,
      clickOpens: true,
      onChange: (dates, _str, inst) =>
        onChangeRef.current(dates.map((d) => iso(inst, d))),
    }) as Instance;
    fpRef.current = fp;
    return () => fp.destroy();
    // Mount-only: later prop changes are handled by the sync effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the allowed-date set in sync as the backend list loads/changes.
  useEffect(() => {
    fpRef.current?.set('enable', enabledDates);
  }, [enabledDates]);

  // Reflect external value changes (e.g. loading a saved view) without echoing back onChange
  // or fighting the user mid-selection.
  useEffect(() => {
    const fp = fpRef.current;
    if (!fp) return;
    const current = fp.selectedDates.map((d) => iso(fp, d));
    if (!sameSet(current, value)) fp.setDate(value, false);
  }, [value]);

  return (
    <TextInput
      ref={inputRef}
      id={id}
      size={size}
      labelText={labelText}
      placeholder={placeholder}
      defaultValue="" // flatpickr owns the displayed value
      readOnly // dates are chosen from the constrained calendar, not typed
    />
  );
}
